import { Combo } from 'types/combo';
import Select, { createFilter, DropdownIndicatorProps } from 'react-select';
import { useMaterialUIController } from 'context';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Igual ao AutoCompleteSelect, mas fora de formulários e com ícones personalizados no dropdown.

type AutoCompleteSelectProps = {
    options: Combo[];
    value?: number | string | null;
    onChange: (value: Combo['value'] | null) => void;
    placeholder?: string;
    disabled?: boolean;
    dropdownIcon?: React.ReactElement;
    isClearable?: boolean;
};

export function StandaloneAutoCompleteSelect({ options, value, onChange, placeholder, disabled = false, dropdownIcon, isClearable = false }: AutoCompleteSelectProps) {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const selectedOption = options.find((option) => option.value === value) || null;

    // Só os eventos que o react-select usa para abrir o menu; o resto do innerProps não serve a um Box.
    const DropdownIndicator = ({ innerProps: { onMouseDown, onTouchEnd } }: DropdownIndicatorProps<Combo, false>) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 8px',
                    fontSize: '18px',
                }}
                aria-hidden
                onMouseDown={onMouseDown}
                onTouchEnd={onTouchEnd}
            >
                {dropdownIcon}
            </Box>
        );
    };

    return (
        <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            components={dropdownIcon ? { DropdownIndicator } : {}}
            options={options}
            filterOption={createFilter({ stringify: (option) => option.label })}
            onChange={(option) => { onChange(option ? option.value : null) }}
            value={selectedOption}
            placeholder={placeholder ?? t('general.selecione')}
            isDisabled={disabled}
            isClearable={isClearable}
            styles={{
                clearIndicator: (base) => ({
                    ...base,
                    color: darkMode ? '#b8c5d1' : '#6c757d',
                    ':hover': {
                        color: darkMode ? '#ffffff' : '#000000',
                    },
                }),
                control: (base) => ({
                    ...base,
                    background: disabled
                        ? darkMode
                            ? '#c4c4c4'
                            : '#c4c4c4'
                        : darkMode
                        ? '#141c2b'
                        : '#FFFFFF',
                    border: disabled
                        ? darkMode
                            ? '1px solid #405568'
                            : '1px solid #AAAAAA'
                        : darkMode
                        ? '1px solid #405568'
                        : '1px solid lightgray',
                    borderRadius: '5px',
                    minWidth: '155px',
                    height: '43px',
                    color: darkMode ? '#ffffff' : '#000000',
                }),
                placeholder: (base) => ({
                    ...base,
                    fontSize: '14px',
                    color: darkMode ? '#b8c5d1' : '#6c757d',
                }),
                singleValue: (base) => ({
                    ...base,
                    fontSize: '14px',
                    color: darkMode
                        ? disabled
                            ? '#475468'
                            : '#ffffff'
                        : disabled
                        ? '#475468'
                        : '#000000',
                }),
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                }),
                menu: (base) => ({
                    ...base,
                    fontSize: '14px',
                    backgroundColor: darkMode ? '#1b2536' : '#FFFFFF',
                    color: darkMode ? '#ffffff' : '#000',
                    zIndex: 9999,
                }),
                option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused
                        ? darkMode
                            ? '#26324a'
                            : '#e9ecef'
                        : darkMode
                        ? '#1b2536'
                        : '#FFFFFF',
                    color: darkMode ? '#ffffff' : '#000000',
                    ':hover': {
                        backgroundColor: darkMode ? '#26324a' : '#e9ecef',
                    },
                }),
                input: (base) => ({
                    ...base,
                    fontSize: '14px',
                    color: darkMode ? '#ffffff' : '#000000',
                }),
            }}
        />
    );
}